import {
  SITE_COLOR,
  SITE_EASE,
  SITE_MEDIA,
  SITE_VAR,
} from "src/domains/shared/constants/styles/site";

/** 언론보도 목록·상세가 함께 쓰는 값 — 시안 news.html 그대로 */
export const NEWS_META_STYLES = {
  fontSize: "15px",
  color: SITE_COLOR.ink3,
  fontWeight: 600,
  fontVariantNumeric: "tabular-nums",
} as const;

/** 상세 본문 사진 아래 사진 설명·사용권 표기(시안 `.art figcaption`) */
export const NEWS_CAPTION_STYLES = {
  marginTop: "12px",
  fontSize: "14px",
  lineHeight: 1.6,
  color: SITE_COLOR.ink3,
} as const;

/** 사진이 만료됐을 때 그 칸을 채우는 대체 표시 — 칸 크기는 그대로 둔다 */
export const NEWS_IMAGE_FALLBACK_STYLES = {
  width: "100%",
  height: "100%",
  minHeight: "160px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  fontSize: "14px",
  color: SITE_COLOR.ink2,
  textAlign: "center",
  background: SITE_COLOR.panel,
} as const;

export const NEWS_LIST_STYLES = {
  head: {
    paddingTop: "clamp(64px,12vw,176px)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "24px",
    flexWrap: "wrap",
  },
  count: { fontSize: "15px", color: SITE_COLOR.ink3, fontWeight: 600 },
  list: { paddingBlock: SITE_VAR.section, paddingTop: 0 },
  empty: { paddingBlock: "64px", textAlign: "center", color: SITE_COLOR.ink2 },
  feature: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1.25fr) minmax(0,1fr)",
    gap: "clamp(32px,5.6vw,88px)",
    alignItems: "center",
    marginTop: "clamp(40px,5.6vw,80px)",
    paddingTop: "clamp(36px,4.4vw,64px)",
    borderTop: `1px solid ${SITE_COLOR.line}`,
    cursor: "pointer",
    "&:hover [data-news-image] img": { transform: "scale(1.04)" },
    "&:hover h2": {
      textDecoration: "underline",
      textDecorationThickness: "2px",
      textUnderlineOffset: "6px",
    },
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr" },
    // 가로 터치 화면: 넓은 화면처럼 사진 왼쪽·글 오른쪽 — 사진(16:10) 높이가 머리말·여백을 뺀 화면 높이에 들어오게 칸 폭을 줄인다
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns:
        "minmax(0,min(56%,calc((100svh - 140px) * 1.6))) minmax(0,1fr)",
      gap: "clamp(24px,4vw,48px)",
    },
  },
  image: {
    borderRadius: SITE_VAR.radiusImage,
    overflow: "hidden",
    aspectRatio: "16/10",
    background: SITE_COLOR.panel,
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: `transform .8s ${SITE_EASE.out}`,
    },
  },
  title: {
    fontSize: "clamp(24px,2.5vw,34px)",
    lineHeight: 1.38,
    fontWeight: 700,
    letterSpacing: "-.025em",
    margin: "16px 0 20px",
    // 상세 제목과 같이 글자 단위로, 줄을 칸 끝까지 채운다(press-news)
    textWrap: "pretty",
    wordBreak: "normal",
  },
  summary: {
    color: SITE_COLOR.ink2,
    fontSize: "clamp(16px,1.3vw,18px)",
    lineHeight: 1.7,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflow: "hidden",
  },
  more: { marginTop: "28px" },
} as const;

export const NEWS_DETAIL_STYLES = {
  article: {
    // 본문 폭(712px)은 여백과 따로 정한다 — 여백이 폭에 따라 늘어도(marketing-site-shell) 넓은 화면의 본문 폭은 그대로
    maxWidth: `calc(712px + ${SITE_VAR.gutter} * 2)`,
    marginInline: "auto",
    paddingInline: SITE_VAR.gutter,
    paddingTop: "clamp(56px,8vw,120px)",
    paddingBottom: "clamp(96px,11vw,176px)",
  },
  back: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "15px",
    fontWeight: 600,
    color: SITE_COLOR.ink2,
    // 손가락으로 누르는 높이 44px
    padding: "10px 0",
    "&:hover": { color: SITE_COLOR.ink },
  },
  /** 제목 아래 기사 정보 한 줄 — 밑줄로 본문과 나눈다(신문 기사 머리, press-news) */
  meta: {
    ...NEWS_META_STYLES,
    paddingBottom: "clamp(20px,2.4vw,28px)",
    borderBottom: `1px solid ${SITE_COLOR.line}`,
    marginBottom: "clamp(28px,3vw,40px)",
  },
  title: {
    fontSize: "clamp(28px,3.2vw,42px)",
    lineHeight: 1.35,
    fontWeight: 700,
    letterSpacing: "-.03em",
    margin: "clamp(32px,4vw,56px) 0 clamp(16px,1.8vw,24px)",
    // balance 는 줄 길이를 맞추느라 본문 칸 폭보다 일찍 꺾어 제목이 왼쪽으로 쏠려 보인다 — 칸을 채우고 끝줄 외톨이만 피한다.
    textWrap: "pretty",
    // 사이트 루트의 한국어 keep-all(낱말 단위)을 풀어 글자 단위로 줄을 바꾼다(press-news)
    wordBreak: "normal",
  },
  figure: {
    margin: "0 0 clamp(28px,3vw,40px)",
    "& img": {
      width: "100%",
      height: "auto",
      borderRadius: SITE_VAR.radiusImage,
      background: SITE_COLOR.panel,
    },
    // 가로 터치 화면: 사진 높이를 머리말 아래 화면 안으로 줄이고 비율 그대로 가운데에
    [SITE_MEDIA.landscapeTouch]: {
      "& img": {
        display: "block",
        width: "auto",
        maxWidth: "100%",
        maxHeight: "calc(100svh - 120px)",
        marginInline: "auto",
      },
    },
  },
  heading: {
    fontSize: "clamp(20px,1.8vw,24px)",
    fontWeight: 700,
    lineHeight: 1.5,
    margin: "0 0 .8em",
  },
  paragraph: {
    fontSize: "clamp(17px,1.35vw,19px)",
    lineHeight: 1.85,
    color: SITE_COLOR.ink,
    margin: "0 0 1.2em",
  },
  list: {
    fontSize: "clamp(17px,1.35vw,19px)",
    lineHeight: 1.85,
    color: SITE_COLOR.ink,
    listStyle: "disc",
    paddingLeft: "1.3em",
    margin: "0 0 1.2em",
    "& li + li": { marginTop: ".3em" },
  },
  mail: {
    color: SITE_COLOR.ink3,
    fontSize: "15px",
    lineHeight: 1.85,
    margin: "0 0 1.2em",
  },
  /** 번역본 기사의 번역 안내 한 줄 — 본문 끝, 「원문보기」 위 */
  translated: {
    color: SITE_COLOR.ink3,
    fontSize: "15px",
    lineHeight: 1.7,
  },
  actions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    marginTop: "clamp(32px,4vw,48px)",
    paddingTop: "28px",
    borderTop: `1px solid ${SITE_COLOR.line}`,
    [SITE_MEDIA.phone]: { justifyContent: "flex-end" },
  },
} as const;
